import { ChangeDetectorRef, Component, OnInit, ViewChild, Input } from '@angular/core';
import { IonModal, ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import { GeocodingService } from "../../../utils/leaflet/service/geocoding.service";
import { TechnicalDataService } from "../technical-data.service";
import { MapService } from "../../../utils/leaflet/service/map.service";
import { DataManagementService } from "../../data-management/data-management.service";
import { ToastrService } from "ngx-toastr";
import { SigninService } from "../../../signin/signin.service";
import { ChartFullscreenService } from "../../../utils/chart-fullscreen/chart-fullscreen.service";
import { MapNavigationService } from '../../../utils/map-top-toolbar/map-navigation.service';
import { GlobalService } from '../../../global.config';
import {
  DeviceCarb,
  DeviceOpt,
  DeviceTemp,
  ExportTempVariation,
  RepFuelVariation,
  TechnicalData
} from "../../../model/technical-data";
import { Subscription } from "rxjs";
import { Group, Vehicule } from "../../../model/data-managemnt";
import { ModalDirective } from "ngx-bootstrap/modal";
import * as Highcharts from 'highcharts/highstock';
import HighchartsMore from 'highcharts/highcharts-more';
HighchartsMore(Highcharts);
import HichartsExportingData from 'highcharts/modules/export-data';
HichartsExportingData(Highcharts);
import { formatDate } from "@angular/common";
import { IDropdownSettings } from "ng-multiselect-dropdown";
import * as L from 'leaflet';
import { Icon, Marker } from 'leaflet';

@Component({
  selector: 'app-temp-data',
  templateUrl: './temp-data.component.html',
  styleUrls: ['./temp-data.component.scss'],
})
export class TempDataComponent implements OnInit {
  @Input() overlayOnly = false;
  @ViewChild('startDateModal') startDateModal: IonModal;
  @ViewChild('endDateModal') endDateModal: IonModal;

  formNotShown = false;
  fullscreenChartId: string | null = null;

  private startPickerState: { date: string; hour: number; minute: number } | null = null;
  private endPickerState: { date: string; hour: number; minute: number } | null = null;
  private startOpenSnapshot: { date: string; hour: number; minute: number } | null = null;
  private endOpenSnapshot: { date: string; hour: number; minute: number } | null = null;
  private startDismissTimer: ReturnType<typeof setTimeout>;
  private endDismissTimer: ReturnType<typeof setTimeout>;

  selected: any = {
    startDate: new Date(new Date().setHours(0, 0, 0, 0) + (60 * 60 * 1000)).toISOString(),
    endDate: new Date(new Date().setHours(23, 59, 59, 999) + (60 * 60 * 1000)).toISOString(),
  };

  //Chart vars
  chart: any;
  options: any;
  serieName: any;

  //Device Options
  deviceopt: DeviceOpt;
  //Device Carburant
  devicecarb: DeviceCarb;
  deviceTemp: DeviceTemp;
  AllGroups: Subscription;
  vehicule: Vehicule;
  technicaldata: TechnicalData[];
  technicaldataReport15min: TechnicalData[];
  repFuelVariation: RepFuelVariation[] = [];
  variationList: ExportTempVariation[] = new Array();

  isResultLoaded: boolean = true;
  volume: number;
  useTemp: boolean = false;
  map: any;
  dropdownList = [];
  isGroupLoaded = false;

  // --- Map lifecycle (mirrors TechnicalDataComponent / FuelDataComponent) ---
  private tempReportMap: L.Map | null = null;
  private tempReportMarker: Marker | null = null;
  private tempReportBaseMaps: Record<string, L.TileLayer> | null = null;
  private mapLayerSubscription?: Subscription;

  @ViewChild('mapModal') public mapModal: ModalDirective;

  optionsDropdownSettings: IDropdownSettings = {
    singleSelection: false,
    idField: 'item_id',
    textField: 'item_text',
    enableCheckAll: true,
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    itemsShowLimit: 3,
    allowSearchFilter: true
  };

  selectedOptionItems: { item_text: string; item_id: number }[] = [];

  get hasFilteredResults(): boolean {
    return !!this.options;
  }

  get showDefaultFilteredLayout(): boolean {
    return this.hasFilteredResults && !this.fullscreenChartId;
  }

  closeFilterForm(): void {
    if (this.hasFilteredResults) {
      this.formNotShown = true;
      setTimeout(() => (this.options as any)?.reflow?.(), 200);
      return;
    }
    void this.close();
  }

  closePopup(): void {
    this.closeFilterForm();
  }

  constructor(private geocodingService: GeocodingService, private technicalDataService: TechnicalDataService, private mapService: MapService,
              public dataManagementService: DataManagementService, public toastr: ToastrService, public signinService: SigninService,
              private router: Router, private chartFullscreenService: ChartFullscreenService,
              private mapNavigationService: MapNavigationService, private modalController: ModalController,
              private globalService: GlobalService, private cdr: ChangeDetectorRef) {
  }

  ionViewWillEnter() {
    this.dataManagementService.ensureSelectedVehicle();
  }

  ionViewWillLeave() {
    this.exitChartFullscreen();
    this.destroyTempReportMap();
  }

  isChartFullscreen(chartId: string): boolean {
    return this.fullscreenChartId === chartId;
  }

  async toggleChartFullscreen(chartId: string, chart: any): Promise<void> {
    const exiting = this.fullscreenChartId === chartId;

    if (exiting) {
      // Leave fullscreen → restore default filtered layout (graph + map + table).
      this.destroyTempReportMap();
      this.fullscreenChartId = null;
      this.formNotShown = true;
      this.cdr.detectChanges();
      await this.chartFullscreenService.exitLandscape();
      this.cdr.detectChanges();
      setTimeout(() => this.restoreFilteredResultsLayout(), 50);
      setTimeout(() => this.restoreFilteredResultsLayout(), 400);
      setTimeout(() => this.restoreFilteredResultsLayout(), 800);
      return;
    }

    // Enter fullscreen — tear down map so Leaflet is not left on a hidden node.
    this.destroyTempReportMap();
    if (this.fullscreenChartId) {
      this.fullscreenChartId = null;
      await this.chartFullscreenService.exitLandscape();
    }
    this.fullscreenChartId = chartId;
    this.cdr.detectChanges();
    await this.chartFullscreenService.enterLandscape();
    this.chartFullscreenService.scheduleReflow(chart);
  }

  private async exitChartFullscreen(): Promise<void> {
    if (!this.fullscreenChartId) {
      return;
    }
    this.destroyTempReportMap();
    this.fullscreenChartId = null;
    this.formNotShown = true;
    this.cdr.detectChanges();
    await this.chartFullscreenService.exitLandscape();
    this.chartFullscreenService.scheduleReflow(this.options as any);
    setTimeout(() => this.restoreFilteredResultsLayout(), 50);
    setTimeout(() => this.restoreFilteredResultsLayout(), 400);
  }

  /** Rebuild the post-filter layout: graph + Position du point map + table (same as Technical/Carburant). */
  private restoreFilteredResultsLayout(): void {
    if (!this.hasFilteredResults) {
      return;
    }

    this.formNotShown = true;
    this.cdr.detectChanges();

    const chartEl = document.getElementById('container') as HTMLElement | null;
    if (chartEl) {
      const targetH = Math.max(160, Math.round(window.innerHeight * 0.28));
      chartEl.style.height = `${targetH}px`;
      chartEl.style.maxHeight = `${targetH}px`;
      chartEl.style.minHeight = '0';
      const w = chartEl.clientWidth || Math.round(window.innerWidth - 24);
      if ((this.options as any)?.setSize) {
        (this.options as any).setSize(w, targetH, false);
      } else {
        (this.options as any)?.reflow?.();
      }
    }

    // Map was removed from DOM during fullscreen — recreate the default point.
    if (!this.tempReportMap && document.getElementById('map')) {
      this.showInitialMapPoint();
    } else if (this.tempReportMap) {
      try {
        this.tempReportMap.invalidateSize(true);
      } catch {
        this.destroyTempReportMap();
        this.showInitialMapPoint();
      }
    }
  }

  onDateModalOpen(type: 'start' | 'end') {
    const value = type === 'start' ? this.selected.startDate : this.selected.endDate;
    const snapshot = this.toPickerState(value);

    if (type === 'start') {
      this.startPickerState = null;
      this.startOpenSnapshot = snapshot;
      clearTimeout(this.startDismissTimer);
    } else {
      this.endPickerState = null;
      this.endOpenSnapshot = snapshot;
      clearTimeout(this.endDismissTimer);
    }
  }

  private toPickerState(value: string) {
    const parsed = new Date(value);
    return {
      date: `${parsed.getFullYear()}-${parsed.getMonth()}-${parsed.getDate()}`,
      hour: parsed.getHours(),
      minute: parsed.getMinutes(),
    };
  }

  private handleDateTimePickerChange(type: 'start' | 'end', state: { date: string; hour: number; minute: number }) {
    const modal = type === 'start' ? this.startDateModal : this.endDateModal;
    const openSnapshot = type === 'start' ? this.startOpenSnapshot : this.endOpenSnapshot;

    if (type === 'start') {
      this.startPickerState = state;
    } else {
      this.endPickerState = state;
    }

    // Ignore the change fired right after opening (value unchanged from open).
    if (openSnapshot &&
      openSnapshot.date === state.date &&
      openSnapshot.hour === state.hour &&
      openSnapshot.minute === state.minute) {
      return;
    }

    // Close shortly after the user stops choosing (date then time resets the timer).
    this.scheduleDateModalDismiss(type, modal);
  }

  private scheduleDateModalDismiss(type: 'start' | 'end', modal: IonModal | null | undefined) {
    const timer = setTimeout(() => {
      this.resetDatePickerState(type);
      void modal?.dismiss();
    }, 500);

    if (type === 'start') {
      clearTimeout(this.startDismissTimer);
      this.startDismissTimer = timer;
    } else {
      clearTimeout(this.endDismissTimer);
      this.endDismissTimer = timer;
    }
  }

  private resetDatePickerState(type: 'start' | 'end') {
    if (type === 'start') {
      this.startPickerState = null;
      this.startOpenSnapshot = null;
      clearTimeout(this.startDismissTimer);
    } else {
      this.endPickerState = null;
      this.endOpenSnapshot = null;
      clearTimeout(this.endDismissTimer);
    }
  }

  dateHandle(event: any, type: string) {
    const value = event.detail.value;
    if (!value) {
      return;
    }
    const state = this.toPickerState(value);
    if (type === 'start') {
      this.selected.startDate = value;
      this.handleDateTimePickerChange('start', state);
    } else {
      this.selected.endDate = value;
      this.handleDateTimePickerChange('end', state);
    }
  }

  async close() {
    this.destroyTempReportMap();
    this.mapNavigationService.setToolbarHidden(false);
    if (this.overlayOnly) {
      this.mapService.preventMapDestroy = false;
      try {
        await this.modalController.dismiss();
      } catch {}
      this.mapNavigationService.requestRealtimeMap();
      return;
    }
    try {
      await this.modalController.dismiss();
    } catch {
      this.router.navigate(['/client']);
      return;
    }
    this.mapNavigationService.requestRealtimeMap();
  }

  ngOnInit() {
    this.mapNavigationService.setToolbarHidden(true);
    this.dropdownList = [{ item_id: 0, item_text: 'Temperature' }];
    this.selectedOptionItems = [{ item_id: 0, item_text: 'Temperature' }];
    if (this.overlayOnly) {
      this.formNotShown = false;
      this.mapService.preventMapDestroy = true;
    }

    // Keep the map's base layer in sync with the layer picker, same as Technical/Carburant.
    this.mapLayerSubscription = this.mapService.mapLayerChange$.subscribe((layerKey) => {
      if (this.tempReportMap) {
        this.mapService.applyMapLayerToMap(this.tempReportMap, layerKey);
      }
    });

    this.dataManagementService.GroupsLoaded$.subscribe(groups => this.onGroupLoaded(groups));
    this.dataManagementService.init();
  }

  onGroupLoaded(groups: Group[]) {
    if (this.dataManagementService.groups) {
      this.isGroupLoaded = true;
    }
  }

  ngOnDestroy() {
    this.mapLayerSubscription?.unsubscribe();
    this.mapNavigationService.setToolbarHidden(false);
    this.resetDatePickerState('start');
    this.resetDatePickerState('end');
    if (this.AllGroups) this.AllGroups.unsubscribe();
  }

  //check if the series is empty or not
  ChartSeries(data: any = []) {
    let ok: boolean;
    data.forEach(dt => {
      ok = dt.y == undefined ? false : true;
    });
    return ok;
  }

  //Return the position of Yaxis
  PositionYaxis(data: any, option: string): number {
    for (let i = 0; i < data.length; i++) {
      if (data[i].title.text === option) {
        return i;
      }
    }
    return -1;
  }

  displayChart() {
    this.isResultLoaded = false;
    this.dropdownList = [{ item_id: 0, item_text: 'Temperature' }];
    this.selectedOptionItems = [{ item_id: 0, item_text: 'Temperature' }];
    this.useTemp = false;
    this.isResultLoaded = false;
    this.technicalDataService.getDeviceTemp(this.dataManagementService.selectedVehicule.device.idDevice).subscribe(resp => {
      this.deviceTemp = resp;
      this.dropdownList = [{ item_id: 0, item_text: 'Temperature' }];
      this.selectedOptionItems = [{ item_id: 0, item_text: 'Temperature' }];
      this.isResultLoaded = true;
    })

    // Options vars
    let temp = [];
    let ranges = [];
    let door = [];
    let accum_odo = [];
    let door2 = [];

    this.variationList = [];

    this.technicaldataReport15min = [];
    //Get Fuel Data and fill all options — always request latitude/longitude so the
    //"Position du point" map works, same as Technical/Carburant.
    this.technicalDataService.getFuelDetail(this.dataManagementService.selectedVehicule.device.idDevice, {
      startDate: this.selected.startDate,
      endDate: this.selected.endDate
    }, "temp ,di1, accum_odo, di2, ignition, latitude, longitude").subscribe(res => {
      this.technicaldata = res;
      if (this.technicaldata.length == 0) {
        this.toastr.warning("Pas de données à afficher");
      }

      let lastInvalid = false;
      let lastTemp = -1000;

      let maxTemp: number = -1000;
      let minTemp: number = 1000;

      let cursor = this.selected.startDate;
      let ign = false;
      this.technicaldataReport15min = [];
      this.technicaldata.forEach(stat => {
          if (this.dataManagementService.selectedVehicule.device.opt.useTemp == 1) {
            this.useTemp = true;
            if (stat.date > cursor || ign != stat.ignition) {
              while (stat.date > cursor)
                cursor = cursor + 1000 * 60 * 15;
              ign = stat.ignition;
              this.dataManagementService.getGeocodingPosition(stat, "TechnicalData", this.dataManagementService.selectedVehicule.device.idDevice);


              if (stat.temp != undefined && Math.floor(stat.temp / 256) != 128 && stat.temp != 65535) {
                this.technicaldataReport15min.push(stat);


                for (let i = 0; i < this.technicaldataReport15min.length; i++) {
                  let variation: ExportTempVariation = new ExportTempVariation;
                  variation.date = this.technicaldataReport15min[i].date;
                  variation.lat = this.technicaldataReport15min[i].latitude;
                  variation.lng = this.technicaldataReport15min[i].longitude;
                  if (this.technicaldataReport15min[i].ignition == true) {
                    variation.status = 'drive';
                  } else {
                    variation.status = 'stop';
                  }

                  variation.temp = Math.round((this.technicaldataReport15min[i].temp * 10) / 16 / 10);


                  this.variationList.push(variation);
                }
              }

            }
          }


          let valid = false;
          if (stat.temp > 10000 && stat.temp < 10600) {//Bug Hardware conversion temp négative BLE
            let rawTemp = stat.temp * 100 / 16;
            stat.temp = ((rawTemp - 65536) / 100) * 16;
          }
          if (stat.temp != undefined && Math.floor(stat.temp / 256) != 128 && stat.temp != 65535) {
            if (stat.temp > 32768)
              stat.temp = stat.temp - 65536;
            if (stat.temp / 16 < 125 && stat.temp / 16 > -50) {
              valid = true;
            }
          }
          if (valid && !lastInvalid) {
            let currtemp = Math.floor(stat.temp * 10) / 16 / 10;
            if (currtemp > maxTemp)
              maxTemp = currtemp;
            if (currtemp < minTemp)
              minTemp = currtemp;

            temp.push({
              x: stat.date, y: currtemp
            });
            if (
              this.deviceTemp &&
              this.deviceTemp.isThresholdHighEnable &&
              this.deviceTemp.isThresholdLowEnable
            ) {
              ranges.push([stat.date, this.deviceTemp.thresholdHighValue, this.deviceTemp.thresholdLowValue]);
            }

            lastTemp = Math.floor(stat.temp * 10) / 16 / 10;

          }
          lastInvalid = !valid;
          if (stat.di1 != undefined) {
            door.push({ x: stat.date, y: (stat.di1 == 1 ? 0 : 1) });
          }
          if (stat.accum_odo != undefined) {
            accum_odo.push({ x: stat.date, y: stat.accum_odo });
          }
          if (stat.di2 != undefined) {
            door2.push({ x: stat.date, y: (stat.di2 == 1 ? 0 : 1) });
          }

        }
      )

      let yaxis = [];
      let serie = [];
      let doorDet = false;
      let doorDet2 = false;
      for (let i = 1; i < door.length; i++) {

        if (
          door[i].y > door[i - 1].y ||
          door[i].y < door[i - 1].y
        ) {
          doorDet = true;
          door[i].x = door[i - 1].x;
        }
      }

      for (let j = 1; j < door2.length; j++) {

        if (
          door2[j].y > door2[j - 1].y ||
          door2[j].y < door2[j - 1].y
        ) {
          doorDet2 = true;
          door2[j].x = door2[j - 1].x;
        }
      }

      if (this.ChartSeries(temp) == true) {
        yaxis.push({
          title: {
            text: "temperature",
          },
          labels: {
            format: "{value} " + "°C",
            style: {
              color: "rgb(107, 57, 156)"
            }
          }
        });
        serie.push({
          name: "Temperature(Max:" + (maxTemp != -1000 ? maxTemp : "--") + " °C / Min : " + (minTemp != 1000 ? minTemp : "--") + "°C)",
          data: temp,
          zIndex: 1,
          yAxis: this.PositionYaxis(yaxis, "temperature"),
        });
      }

      if (this.ChartSeries(door) == true && doorDet) {
        yaxis.push({
          title: {
            text: "porte",
            style: {
              color: "rgb(255, 127, 0)"
            }
          },
          labels: {
            style: {
              color: "rgb(255, 127, 0)"
            }
          },
          min: 0,
          alignTicks: false,
          endOnTick: false,
          tickInterval: 1
        });
        serie.push({
          name: "Etat de porte",
          data: door,
          type: "area",
          color: "rgb(255, 127, 0)",
          yAxis: this.PositionYaxis(yaxis, "porte"),
        });

      }

      if (this.ChartSeries(accum_odo)) {
        yaxis.push({
          title: {
            text: "odomètre",
            style: {
              color: "rgb(44, 161, 33)"
            }
          },
          labels: {
            format: "{value} " + "Km",
            style: {
              color: "rgb(44, 161, 33)"

            }
          },
          min: null
        });

        serie.push({
          name: 'odomètre',
          data: accum_odo,
          color: 'rgb(44, 161, 33)',
          tooltip: {
            valueSuffix: "KM",
            valueDecimals: 2
          },
          yAxis: this.PositionYaxis(yaxis, "odomètre"),
        });
      }

      if (this.dataManagementService.selectedVehicule.device.opt.useDoor == 2) {
        if (this.ChartSeries(door2) == true && doorDet2) {
          yaxis.push({
            title: {
              text: "porte 2",
              style: {
                color: "rgb(240, 225, 96)"
              }
            },
            labels: {
              style: {
                color: "rgb(240, 225, 96)"
              }
            },
            min: 0,
            alignTicks: false,
            endOnTick: false,
            tickInterval: 1
          });
          serie.push({
            name: "Etat de porte 2",
            data: door2,
            type: "area",
            color: "rgb(240, 225, 96)",
            yAxis: this.PositionYaxis(yaxis, "porte"),
          });

        }
      }

      //Create a new HighChart
      this.options = new Highcharts.Chart({
        title: { text: "Véhicule: " + this.dataManagementService.selectedVehicule.matricule },
        chart: {
          renderTo: 'container',
          height: 400,
        },

        yAxis: yaxis,

        series: serie,
        xAxis: [{
          type: 'datetime',
          tickInterval: 3600 * 1000,
          labels: {
            formatter: function () {
              return formatDate(
                this.value,
                'dd/MM/yyyy, HH:MM',
                'en-US'
              );
            }
          },
          crosshair: true
        }],
        plotOptions: {
          series: {
            turboThreshold: 100000,
            events: {
              click: (event) => {
                this.getPathDatails(this.dataManagementService.selectedVehicule.device.idDevice, event.point.x, event.point.x)
              },
            },
          }
        },
      });
      this.isResultLoaded = true;

      // Auto-show a default point on the map, exactly like Technical/Carburant do after search.
      this.scheduleInitialMapPoint();
    });

  }

  saveInstance(chartInstance) {
    this.chart = chartInstance;
  }

  onSeriesMouseOver(e) {
    this.serieName = e.context.name;
  }

  chooseGroup(group) {
    this.dataManagementService.selectedGroup = group;;
    if (this.dataManagementService.selectedGroup.vehicules.length > 0 && this.dataManagementService.selectedGroup) {
      this.dataManagementService.selectedVehicule = this.dataManagementService.selectedGroup.vehicules[0];
    }
  }

  chooseVehicule(vehicule) {
    this.dataManagementService.selectedVehicule = vehicule;
  }

  public showChildModal(): void {
    this.mapModal.show();
  }

  public hideChildModal(): void {
    this.mapModal.hide();
  }

  // ---------------------------------------------------------------------
  // Map lifecycle — mirrors TechnicalDataComponent / FuelDataComponent exactly.
  // ---------------------------------------------------------------------

  private destroyTempReportMap(): void {
    if (this.tempReportMarker && this.tempReportMap) {
      this.tempReportMap.removeLayer(this.tempReportMarker);
    }
    this.tempReportMarker = null;

    if (this.tempReportMap) {
      this.tempReportMap.remove();
      this.tempReportMap = null;
    }

    this.tempReportBaseMaps = null;

    const container = document.getElementById('map');
    if (container) {
      container.replaceChildren();
      delete (container as HTMLElement & { _leaflet_id?: number })._leaflet_id;
    }
  }

  private refreshTempReportMapSize(map: L.Map): void {
    map.invalidateSize(true);
    setTimeout(() => map.invalidateSize(true), 150);
    setTimeout(() => map.invalidateSize(true), 450);
  }

  private createTempReportMap(): L.Map | null {
    const container = document.getElementById('map');
    if (!container) {
      return null;
    }

    this.mapService.ensureBaseMaps();
    this.tempReportBaseMaps = this.mapService.createFreshBaseMapLayers() as Record<string, L.TileLayer>;
    const layerKey = this.mapService.resolveMapLayerKey();
    const tileLayer = this.tempReportBaseMaps[layerKey] ?? this.tempReportBaseMaps.OpenStreetMap;

    const map = L.map(container, {
      zoomControl: false,
      center: L.latLng(36.850457, 10.225097),
      zoom: 6,
      minZoom: 3,
      maxZoom: 20,
      layers: [tileLayer],
    } as L.MapOptions);

    L.control.layers(this.tempReportBaseMaps, undefined, { position: 'bottomright' }).addTo(map);
    L.control.zoom({ position: 'bottomright' }).addTo(map);

    this.tempReportMap = map;
    return map;
  }

  private buildTempMarkerIcon(ignition: boolean): Icon {
    const base = (this.globalService.imagesCarsDir || this.globalService.getimagesCarsDir?.() || '').replace(/\/$/, '');
    const iconUrl = `${base}/${ignition ? 'c1x0.png' : 'c3x0.png'}`;
    return new Icon({
      iconUrl,
      iconSize: [40, 40],
      shadowSize: [40, 40],
      iconAnchor: [20, 20],
      shadowAnchor: [20, 20],
      popupAnchor: [10, -10],
    });
  }

  private hasValidCoordinates(data: any): boolean {
    const lat = Number(data?.latitude);
    const lng = Number(data?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
  }

  private showTempPointOnMap(data: any): void {
    const map = this.tempReportMap;
    if (!map || !this.hasValidCoordinates(data)) {
      this.toastr.warning('Aucune position disponible pour ce point');
      return;
    }

    const lat = Number(data.latitude);
    const lng = Number(data.longitude);

    if (this.tempReportMarker) {
      map.removeLayer(this.tempReportMarker);
    }

    const marker = new Marker([lat, lng], {
      icon: this.buildTempMarkerIcon(!!data.ignition),
    });
    let popup = '<div><b>Position</b></div>';

    this.geocodingService.inverseGeoconding(lat, lng, 7).subscribe(adress => {
      popup += `<div>${adress.display_name}</div>`;
      marker.bindPopup(popup);
    });

    marker.on('mouseover', () => marker.openPopup());
    marker.addTo(map);
    this.tempReportMarker = marker;

    map.setView([lat, lng], 15, { animate: false });
    this.refreshTempReportMapSize(map);
  }

  private cachedPointData(source?: TechnicalData): { latitude: number; longitude: number; ignition?: boolean } | null {
    if (!source || !this.hasValidCoordinates(source)) {
      return null;
    }
    return {
      latitude: Number(source.latitude),
      longitude: Number(source.longitude),
      ignition: !!source.ignition,
    };
  }

  private findReportEntryForPoint(dateD: any, dateF: any): TechnicalData | undefined {
    const startMs = new Date(dateD).getTime();
    const endMs = new Date(dateF).getTime();
    return this.technicaldataReport15min?.find((d) => {
      const t = new Date(d.date).getTime();
      return t >= startMs && t <= endMs;
    });
  }

  private initTempReportMapForPoint(idDevice: number, dateD: any, dateF: any, cachedEntry?: TechnicalData): void {
    const mapCard = document.getElementById('map-card');
    if (mapCard) {
      mapCard.classList.add('visible');
    }

    this.destroyTempReportMap();
    this.cdr.detectChanges();

    setTimeout(() => {
      const map = this.createTempReportMap();
      if (!map) {
        return;
      }

      const showPoint = (data: { latitude: number; longitude: number; ignition?: boolean }): void => {
        map.whenReady(() => {
          this.refreshTempReportMapSize(map);
          this.showTempPointOnMap(data);
        });
      };

      const cached = this.cachedPointData(cachedEntry);
      if (cached) {
        showPoint(cached);
        return;
      }

      this.technicalDataService.getFuelDetail(idDevice, {
        startDate: dateD,
        endDate: dateF
      }, ['latitude', 'longitude', 'ignition', 'temp']).subscribe({
        next: (resp) => {
          const data = Array.isArray(resp) ? resp.find(item => !!item) || resp[0] : resp;
          if (!data || !this.hasValidCoordinates(data)) {
            this.toastr.warning('Aucune position disponible pour ce point');
            return;
          }
          if (this.tempReportMap) {
            showPoint(data);
          }
        },
        error: () => {
          this.toastr.error('Aucune position disponible pour ce point');
        }
      });
    }, 200);
  }

  /** Auto-show the first available point after a search, same as Technical/Carburant. */
  private scheduleInitialMapPoint(): void {
    setTimeout(() => {
      (this.options as any)?.reflow?.();
      this.cdr.detectChanges();
      this.showInitialMapPoint();
    }, 500);
  }

  private showInitialMapPoint(): boolean {
    const entry = this.technicaldataReport15min?.find(d => this.hasValidCoordinates(d));
    if (!entry) {
      return false;
    }
    this.initTempReportMapForPoint(
      this.dataManagementService.selectedVehicule.device.idDevice,
      entry.date,
      entry.date,
      entry
    );
    return true;
  }

  getPathDatails(idDevice: number, dateD: any, dateF: any) {
    this.formNotShown = true;
    const start = new Date(dateD);
    const end = new Date(dateF || dateD);
    const startDate = new Date(start.getTime() - 2 * 60 * 1000).toISOString();
    const endDate = new Date(end.getTime() + 2 * 60 * 1000).toISOString();
    const cachedEntry = this.findReportEntryForPoint(startDate, endDate);
    this.initTempReportMapForPoint(idDevice, startDate, endDate, cachedEntry);
  }

  onMapReady(map) {
    setTimeout(() => {
      map.invalidateSize(true)
    }, 300)
  }

  getColor(ign): string {
    if (ign)
      return "rgba(0,200,20,0.2)";
    return "rgba(200,0,20,0.2)";
  }

}
